/* Copyright (c) 2020-present CloudIO Inc */

import { Popover, Theme, Slider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Border, BorderProp, BorderStyle } from 'componentregistry';
import ColorPicker from 'custom/ColorPicker';
import SelectInput from 'custom/SelectInput';
import Icon from 'icons/Icon';
import React, { memo } from 'react';
import { useTheme } from 'theme/Theme';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const borderStyles = ['solid', 'dotted', 'dashed'];
const borderStyleOptions = borderStyles.map((style) => ({
  value: style,
  label: `${style.substring(0, 1).toUpperCase()}${style.substring(1)}`,
}));

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
    position: 'relative',
  },
  color: {
    marginLeft: 24,
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
  },
  label: {
    flex: 0,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  option: {
    alignSelf: 'center',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    flex: 1,
    fontSize: '0.8rem',
    outline: 'none',
    overflow: 'hidden',
    padding: '4px 0',
    position: 'relative',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    transition: 'color 0.2s',
    '& > svg': {
      height: '1em',
      width: '1em',
    },
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      right: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  selected: {},
  slider: {
    flex: 2,
    height: '30px',
    overflow: 'visible',
    padding: '0 16px',
    zIndex: 1,
  },
  expander: {
    margin: '0 8px',
    outline: 'none',
    width: 16,
    '& > svg': {
      fill: theme.palette.text.secondary,
    },
    '&$selected': {
      '& > svg': {
        fill: theme.palette.primary.main,
      },
    },
    flexShrink: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  right: {
    textAlign: 'right',
    paddingRight: 8,
  },
  input: {
    border: `4px solid ${theme.palette.background.default}`,
    lineHeight: '22px',
    padding: 0,
    textAlign: 'end',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '48px',
    width: '48px',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  },
  styleSelect: {
    width: 80,
  },
}));

function BorderValueFn({
  label,
  value,
  onChange,
  expanded,
  property,
  isViewOnlyUser = false,
}: {
  label: string;
  value?: BorderStyle;
  onChange: (val?: BorderStyle, expand?: boolean) => void;
  expanded: boolean;
  property: string;
  isViewOnlyUser?: boolean;
}) {
  const classes = useStyles();
  const theme = useTheme();
  const hasValue = value !== undefined;
  const disabled = label === 'all' ? expanded : !hasValue;
  const [anchorEl, setAnchorEl] = React.useState<Element | undefined>(
    undefined,
  );
  const open = Boolean(anchorEl);

  const handleClick = (event: React.SyntheticEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  function onClick() {
    if (label !== 'all') {
      onChange(value !== undefined ? undefined : { width: 0, style: 'solid' });
    }
    if (label === 'all') {
      onChange(
        expanded
          ? { ...value, width: 0, style: 'solid' }
          : {
              ...value,
              width: value?.width ?? 0,
              style: value?.style ?? 'solid',
            },
        true,
      );
    }
  }
  let c = value?.borderColor;
  if (c === 'rgba(0, 0, 0, 0.12)') {
    c = theme.palette.divider;
  }
  const colorString =
    typeof c === 'object'
      ? `rgba(${c.r},${c.g},${c.b},${c.a})`
      : c ?? theme.palette.divider;
  return (
    <>
      <div data-testid={`${property}-${label}`} className={classes.root}>
        <div
          className={clsx(
            classes.label,
            label !== 'all' ? classes.right : null,
          )}
        >
          {label === 'all'
            ? 'Border'
            : label.charAt(0).toUpperCase() + label.substring(1)}
        </div>
        <div className={classes.inner}>
          <div className={clsx(classes.option, classes.slider)}>
            <Slider
              disabled={disabled || isViewOnlyUser}
              valueLabelDisplay="auto"
              step={1}
              min={0}
              max={20}
              onChange={(_: Event, val: number | number[]) => {
                if (typeof val === 'number') {
                  onChange({
                    ...value,
                    width: val,
                    style: value?.style ?? 'solid',
                  });
                }
              }}
              value={value?.width ?? 0}
            />
          </div>
          <input
            className={clsx(classes.option, classes.input)}
            disabled={disabled || isViewOnlyUser}
            type="number"
            value={value?.width ?? ''}
            onChange={(e) => {
              onChange({
                ...value,
                width: e.target.value ? parseFloat(e.target.value) : 0,
                borderColor: value?.borderColor ?? theme.palette.divider,
              });
            }}
          />
          <SelectInput
            disabled={disabled || isViewOnlyUser}
            value={value?.style ?? 'solid'}
            options={borderStyleOptions}
            onChange={(style) => {
              onChange({ ...value, style, width: value?.width ?? 0 });
            }}
            disableClearable
            className={classes.styleSelect}
            disableUnderline
            variant="standard"
          />
          <Icon
            disabled={isViewOnlyUser}
            iconName="circle"
            iconType="solid"
            color={colorString}
            onClick={disabled ? undefined : handleClick}
          />

          <div
            onKeyDown={(e) => clickOnEnterOrSpace(e, onClick)}
            role="button"
            tabIndex={-1}
            data-tip={
              // eslint-disable-next-line no-nested-ternary
              label !== 'all'
                ? hasValue
                  ? 'Clear'
                  : undefined
                : expanded
                ? 'Clear All'
                : 'Independent Border'
            }
            className={clsx(
              classes.expander,
              label !== 'all' && hasValue ? classes.selected : null,
              label === 'all' && expanded ? classes.selected : null,
            )}
            onClick={onClick}
          >
            {label === 'all' ? (
              <Icon iconName={expanded ? 'compress' : 'expand'} />
            ) : null}
            {label !== 'all' && !hasValue ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zm16 400c0 8.8-7.2 16-16 16H48c-8.8 0-16-7.2-16-16V80c0-8.8 7.2-16 16-16h352c8.8 0 16 7.2 16 16v352z" />
              </svg>
            ) : null}
            {label !== 'all' && hasValue ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M400 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V80c0-26.51-21.49-48-48-48zm0 32c8.823 0 16 7.178 16 16v352c0 8.822-7.177 16-16 16H48c-8.822 0-16-7.178-16-16V80c0-8.822 7.178-16 16-16h352m-34.301 98.293l-8.451-8.52c-4.667-4.705-12.265-4.736-16.97-.068l-163.441 162.13-68.976-69.533c-4.667-4.705-12.265-4.736-16.97-.068l-8.52 8.451c-4.705 4.667-4.736 12.265-.068 16.97l85.878 86.572c4.667 4.705 12.265 4.736 16.97.068l180.48-179.032c4.704-4.667 4.735-12.265.068-16.97z" />
              </svg>
            ) : null}
          </div>
        </div>
      </div>

      <Popover
        id="color"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <ColorPicker
          value={value?.borderColor ?? theme.palette.divider}
          onChange={(borderColor) => {
            onChange({ ...value, borderColor, width: value?.width ?? 0 });
          }}
        />
      </Popover>
    </>
  );
}

function BorderProperty({
  activeItemId,
  pageId,
  property,
  mgr,
  value = getDefaultValues<Border>(mgr, property),
  onChange: setValue,
  isViewOnlyUser = false,
}: PropertyEditorProps<BorderProp, Border>) {
  const expanded = !!(
    value.top !== undefined ||
    value.right !== undefined ||
    value.bottom !== undefined ||
    value.left !== undefined
  );
  const sides = ['all', 'top', 'right', 'bottom', 'left'] as const;

  return (
    <>
      {expanded ? (
        sides.map((side: keyof Border) => (
          <BorderValueFn
            isViewOnlyUser={isViewOnlyUser}
            property={property}
            expanded={expanded}
            key={side}
            label={side}
            value={value[side]}
            onChange={(m, expand) => {
              if (expand) {
                setValue(property, undefined);
              } else if (value && m === undefined) {
                const val = { ...value };
                delete val[side];
                if (Object.keys(val).length) {
                  setValue(property, val);
                } else {
                  setValue(property, undefined);
                }
              } else {
                setValue(property, { ...value, [side]: m });
              }
            }}
          />
        ))
      ) : (
        <BorderValueFn
          isViewOnlyUser={isViewOnlyUser}
          property={property}
          expanded={false}
          key="all"
          label="all"
          value={value.all}
          onChange={(v, expand) => {
            setValue(
              property,
              expand
                ? {
                    top: v,
                    bottom: v,
                    right: v,
                    left: v,
                  }
                : { all: v },
            );
          }}
        />
      )}
    </>
  );
}

export default memo(BorderProperty);
